/**
 * Workstation mappings (1.0.0-rc.9): how editors' computers open an edit location or a storage location, as a list of
 * named mappings (a Windows share or drive, a macOS or Linux mount point), each for one operating system and at most
 * one the default for its OS. A table with Add, Edit and Remove, inline validation (the same rules as the API) and a
 * live preview of a path. Used by Add location, the location cards' dialog and the Edit locations page.
 *
 * rc.9 fixes: Default is a radio within each OS. Ticking one (in the row being edited, or on a kept row) unticks the
 * other default of that OS at once, so the list never shows two.
 */
import { useState } from 'react';
import { Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import {
  MAX_WORKSTATION_MAPPINGS, TARGET_OS, TARGET_OS_LABELS, joinWorkstationPath, normalizeWorkstationPath, setDefaultMapping, workstationMappingProblems,
  type MappingProblem, type TargetOs, type WorkstationMappingDTO,
} from '@prism/contracts';

/** A mapping being edited: `id` is absent for a new one until the API gives it one. */
export type MappingDraft = Omit<WorkstationMappingDTO, 'id'> & { id?: string };

const PLACEHOLDER: Record<TargetOs, string> = { mac: '/Volumes/Edit', windows: '\\\\nas01\\edit or E:\\', linux: '/mnt/edit' };

/** The problems of a list, for the Save button (none: it can be saved). */
export const mappingProblems = (list: MappingDraft[]): MappingProblem[] => workstationMappingProblems(list);

/** A list as the API takes it (the paths normalised as they will be stored). */
export const mappingsBody = (list: MappingDraft[]) => list.map((m) => ({ ...(m.id ? { id: m.id } : {}), name: m.name.trim(), os: m.os, path: normalizeWorkstationPath(m.os, m.path), isDefault: m.isDefault }));

/** What a file looks like through a mapping: a hand-off's media for an edit location, an original for storage. */
export function previewPath(m: Pick<MappingDraft, 'os' | 'path'>, purpose: 'edit' | 'storage'): string | null {
  const base = normalizeWorkstationPath(m.os, m.path);
  if (!base) return null;
  try {
    return purpose === 'edit' ? joinWorkstationPath(base, '<hand-off>', 'media', 'clip.mov') : joinWorkstationPath(base, 'originals', '…', 'clip.mov');
  } catch {
    return null;
  }
}

export function WorkstationMappingsEditor({ value, onChange, purpose }: { value: MappingDraft[]; onChange: (v: MappingDraft[]) => void; purpose: 'edit' | 'storage' }) {
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState<MappingDraft | null>(null);
  // The list as it would be with the open row kept (a new default for an OS takes over from the old one).
  const withDraft = (): MappingDraft[] => {
    if (editing === null || !draft) return value;
    const next = editing >= value.length ? [...value, draft] : value.map((m, i) => (i === editing ? draft : m));
    return draft.isDefault ? setDefaultMapping(next, editing) : next;
  };
  const shown = withDraft();
  const problems = workstationMappingProblems(shown);
  const rowProblems = (i: number) => problems.filter((p) => p.index === i);
  const start = (i: number) => {
    setEditing(i);
    setDraft(i >= value.length ? { name: '', os: value.some((m) => m.os === 'mac') ? 'windows' : 'mac', path: '', isDefault: !value.length } : { ...value[i]! });
  };
  const commit = () => {
    if (editing === null || !draft || rowProblems(editing).length) return;
    onChange(withDraft());
    setEditing(null);
    setDraft(null);
  };
  const cancel = () => { setEditing(null); setDraft(null); };
  const remove = (i: number) => { onChange(value.filter((_, j) => j !== i)); cancel(); };
  /** A kept row's Default: on makes it its OS's only default; off leaves the OS without one. */
  const setDefault = (i: number, on: boolean) => onChange(setDefaultMapping(value, i, on));
  const editRow = (i: number) => {
    const d = draft!;
    const msgs = rowProblems(i);
    const msg = (field: MappingProblem['field']) => msgs.find((p) => p.field === field)?.message;
    const preview = !msg('path') ? previewPath(d, purpose) : null;
    return (
      <tr key={`edit-${i}`} className="mapping-edit" data-testid="mapping-edit-row">
        <td>
          <input className="input" value={d.name} onChange={(e) => setDraft({ ...d, name: e.target.value })} placeholder="e.g. Helsinki Macs" aria-label="Mapping name" maxLength={60} autoFocus data-testid="mapping-name" />
          {d.name.trim() && msg('name') && <span className="error-text" data-testid="mapping-error">{msg('name')}</span>}
        </td>
        <td>
          <select className="select" value={d.os} onChange={(e) => setDraft({ ...d, os: e.target.value as TargetOs })} aria-label="Operating system" data-testid="mapping-os">
            {TARGET_OS.map((o) => <option key={o} value={o}>{TARGET_OS_LABELS[o]}</option>)}
          </select>
        </td>
        <td>
          <input className="input mono" value={d.path} spellCheck={false} onChange={(e) => setDraft({ ...d, path: e.target.value })} placeholder={PLACEHOLDER[d.os]} aria-label="Path"
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') cancel(); }} data-testid="mapping-path" />
          {d.path.trim() && msg('path') && <span className="error-text" data-testid="mapping-error">{msg('path')}</span>}
          {preview && <span className="help" data-testid="mapping-preview">{purpose === 'edit' ? 'media/clip.mov' : 'An original'} opens as <span className="mono">{preview}</span></span>}
        </td>
        <td><input type="checkbox" checked={d.isDefault} onChange={(e) => setDraft({ ...d, isDefault: e.target.checked })} aria-label={`Default for ${TARGET_OS_LABELS[d.os]}`} data-testid="mapping-default" /></td>
        <td className="mapping-actions">
          <button type="button" className="btn primary icon sm" onClick={commit} disabled={msgs.length > 0} aria-label="Keep this mapping" title="Keep this mapping" data-testid="mapping-keep"><Check size={13} /></button>
          <button type="button" className="btn ghost icon sm" onClick={cancel} aria-label="Cancel" title="Cancel"><X size={13} /></button>
        </td>
      </tr>
    );
  };
  return (
    <div className="mappings" data-testid="workstation-mappings">
      <table className="table mappings-table">
        <thead><tr><th>Name</th><th>OS</th><th>Path on the editors' computers</th><th>Default</th><th /></tr></thead>
        <tbody>
          {value.map((m, i) => (editing === i ? editRow(i) : (
            <tr key={m.id ?? `new-${i}`} data-testid="mapping-row" data-name={m.name}>
              <td>{m.name}</td>
              <td>{TARGET_OS_LABELS[m.os]}</td>
              <td className="mono" title={previewPath(m, purpose) ?? undefined}>{m.path}</td>
              <td>
                <label className="toggle-row" style={{ margin: 0 }}>
                  <input type="checkbox" checked={shown[i]!.isDefault} disabled={editing !== null} onChange={(e) => setDefault(i, e.target.checked)} aria-label={`${m.name}: default for ${TARGET_OS_LABELS[m.os]}`} data-testid="mapping-row-default" />
                  {shown[i]!.isDefault ? <span className="pill accent" data-testid="mapping-default-pill">Default</span> : null}
                </label>
              </td>
              <td className="mapping-actions">
                <button type="button" className="btn ghost icon sm" onClick={() => start(i)} disabled={editing !== null} aria-label={`Edit ${m.name}`} title="Edit" data-testid="mapping-edit"><Pencil size={13} /></button>
                <button type="button" className="btn ghost icon sm danger" onClick={() => remove(i)} disabled={editing !== null} aria-label={`Remove ${m.name}`} title="Remove" data-testid="mapping-remove"><Trash2 size={13} /></button>
              </td>
            </tr>
          )))}
          {editing !== null && editing >= value.length && editRow(value.length)}
          {!value.length && editing === null && <tr><td colSpan={5} className="faint">No mappings yet: the timelines use the location's own paths, and editors relink.</td></tr>}
        </tbody>
      </table>
      <button type="button" className="btn sm" onClick={() => start(value.length)} disabled={editing !== null || value.length >= MAX_WORKSTATION_MAPPINGS} data-testid="mapping-add"><Plus size={13} /> Add mapping</button>
      <span className="help">One per office or way of mounting the share: a Windows share (<span className="mono">\\nas01\edit</span>) or drive (<span className="mono">E:\</span>), a Mac volume (<span className="mono">/Volumes/Edit</span>), a Linux mount. The default for each OS is offered first.</span>
    </div>
  );
}
